import type { PortalMeasurement } from '@/lib/api/endpoints/portal';

interface Point { date: string; value: number }

/** Line chart of bodyweight over time, with a dashed goal line if provided. */
export function WeightChart({ measurements, goal }: { measurements: PortalMeasurement[]; goal?: number | null }) {
  const series: Point[] = measurements
    .filter((m) => typeof m.bodyWeight === 'number')
    .map((m) => ({ date: m.date, value: m.bodyWeight as number }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  if (series.length < 2) {
    return <p className="portal-muted">Not enough weigh-ins yet to chart a trend.</p>;
  }

  const W = 320, H = 120, padX = 6, padY = 14;
  const vals = series.map((p) => p.value).concat(typeof goal === 'number' ? [goal] : []);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const n = series.length;
  const x = (i: number) => padX + (i * (W - padX * 2)) / Math.max(1, n - 1);
  const y = (v: number) => H - padY - ((v - min) / span) * (H - padY * 2);
  const pts = series.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const first = series[0]!.value;
  const last = series[n - 1]!.value;
  const delta = Math.round((last - first) * 10) / 10;

  return (
    <div>
      <div className="portal-weight-head">
        <div>
          <span className="portal-weight-current">{last}</span>
          <span className="portal-weight-unit"> lb</span>
        </div>
        <div className="portal-weight-meta">
          <span className={delta <= 0 ? 'portal-weight-down' : 'portal-weight-up'}>
            {delta <= 0 ? '▼' : '▲'} {Math.abs(delta)} lb
          </span>
          {typeof goal === 'number' && <span className="portal-weight-goal">Goal {goal}</span>}
        </div>
      </div>
      <svg className="portal-weight-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Weight over time">
        <polyline points={`${padX},${H - padY} ${pts} ${W - padX},${H - padY}`} fill="rgba(21,165,227,.08)" stroke="none" />
        {typeof goal === 'number' && (
          <line x1={padX} x2={W - padX} y1={y(goal)} y2={y(goal)} stroke="#bbb" strokeWidth="1" strokeDasharray="4 4" />
        )}
        <polyline points={pts} fill="none" stroke="#15a5e3" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={x(n - 1)} cy={y(last)} r="3.5" fill="#15a5e3" />
      </svg>
    </div>
  );
}
